import { useEffect, useState, type ReactNode } from 'react'
import { Avatar } from '../ui'
import { useReducedMotion } from './motion'

// Who acts next, on what, by when, and the one action that moves it.
//
// # WHY IT IS ONE BAR
//
// The spec's rule is that every journey names who acts next. On the screens
// this replaces, that answer was spread over a paragraph about the chain, a
// due date in a table and a button somewhere below. One bar under the track
// puts the person, the thing they confirm and the time left in one line with
// the action beside it.
//
// `progress.value` is the share of the stage's time already used, 0 to 100.
// The bar is green while under half is used, amber past half and critical past
// 85, and a marker sits at now. When the person changes, the line fades to the
// new one rather than jumping, so the reader sees that it moved.

function tone(value: number): string {
  if (value >= 85) return 'kit-crit'
  if (value >= 50) return 'kit-warn'
  return 'kit-ok'
}

export function NextUp({
  who,
  role,
  what,
  due,
  progress,
  action,
}: {
  who: string
  role: string
  what: string
  due: string
  progress?: { value: number; label: string }
  action?: ReactNode
}) {
  const reduced = useReducedMotion()
  const target = progress ? Math.max(0, Math.min(100, progress.value)) : 0
  const [shown, setShown] = useState(reduced ? target : 0)
  useEffect(() => {
    if (reduced) {
      setShown(target)
      return
    }
    // Two frames, so the bar is painted at its old width before it moves.
    let inner = 0
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setShown(target))
    })
    return () => {
      cancelAnimationFrame(outer)
      cancelAnimationFrame(inner)
    }
  }, [target, reduced])

  return (
    <div className="kit-next">
      <div className="kit-next-who" key={who + '|' + role}>
        <Avatar name={who} size={30} />
        <span className="kit-next-text">
          <span>
            <b>
              Next: {who}, {role}
            </b>{' '}
            {what}
          </span>
          {due && <span className="kit-next-due">Due {due}</span>}
        </span>
      </div>
      {progress && (
        <div className="kit-next-sla">
          <div
            className="kit-next-bar"
            role="progressbar"
            aria-label={progress.label}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(target)}
          >
            <span className={tone(target)} style={{ width: shown + '%' }} />
            <em style={{ left: shown + '%' }} />
          </div>
          <small>{progress.label}</small>
        </div>
      )}
      {action && <div className="kit-next-action">{action}</div>}
    </div>
  )
}
